import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InvoicesDueAlert from './InvoicesDueAlert';
import Limit90Alert from './Limit90Alert';
import OpenInvoicesAlert from './OpenInvoicesAlert';
import Limit100Alert from './Limit100Alert';
import { Alert, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NoActiveSubscriptionAlert from './NoActiveSubscriptionAlert';
import Box from '@mui/material/Box';
import { getUsageItemMeterName, getUsageItemsAtThreshold } from '../../utils/subscriptionUsage';

const BottomBar = () => {
  const subscription = useSelector((state) => state.subscription.subscription);
  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState({
    invoicesDue: false,
    noActiveSubscription: false,
    limit100: false,
    openInvoices: false,
    limit90: false,
    limit90Meters: [],
    limit100Meters: [],
  });

  useEffect(() => {
    if (!subscription) {
      return setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: false,
        limit90: false,
        noActiveSubscription: true,
        limit90Meters: [],
        limit100Meters: [],
      });
    }

    const limit100Meters = getUsageItemsAtThreshold(subscription?.usage, 1);
    const isLimitReached = limit100Meters.length > 0;

    const limit90Meters = getUsageItemsAtThreshold(subscription?.usage, 0.9);
    const isLimit90 = limit90Meters.length > 0;

    const shouldShowOpenInvoices =
      !subscription?.is_from_partner && subscription?.unpaid_invoices?.some((invoice) => invoice.status === 'open');

    if (subscription.status === 'past_due') {
      setOpenSnackbar({
        invoicesDue: true,
        limit100: false,
        openInvoices: false,
        limit90: false,
        limit90Meters: [],
        limit100Meters: [],
      });
    } else if (subscription.status !== 'active') {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: false,
        limit90: false,
        noActiveSubscription: true,
        limit90Meters: [],
        limit100Meters: [],
      });
    } else if (isLimitReached) {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: true,
        openInvoices: false,
        limit90: false,
        limit100Meters: limit100Meters.map(getUsageItemMeterName),
        limit90Meters: [],
      });
    } else if (shouldShowOpenInvoices) {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: true,
        limit90: false,
        limit90Meters: [],
        limit100Meters: [],
      });
    } else if (isLimit90) {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: false,
        limit90: true,
        limit90Meters: limit90Meters.map(getUsageItemMeterName),
        limit100Meters: [],
      });
    }
  }, [subscription]);

  const shouldShowSnackbar =
    openSnackbar.invoicesDue ||
    openSnackbar.limit100 ||
    openSnackbar.limit90 ||
    openSnackbar.openInvoices ||
    openSnackbar.noActiveSubscription;

  if (!shouldShowSnackbar) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        mt: 'auto',
        width: '100%',
        zIndex: (theme) => theme.zIndex.appBar,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        p: 0,
      }}
    >
      <Alert
        severity="warning"
        sx={{
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: '60px',
          justifyContent: 'center',
          py: 1,
          px: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={3}
          sx={{
            width: '100%',
            minHeight: '40px',
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {openSnackbar.invoicesDue && (
              <InvoicesDueAlert
                isFromPartner={subscription?.is_from_partner || false}
                partnerEmail={subscription?.partner_org_email || ''}
              />
            )}
            {openSnackbar.openInvoices && (
              <OpenInvoicesAlert
                unpaidInvoices={subscription?.unpaid_invoices || []}
                onClose={() => setOpenSnackbar({ ...openSnackbar, openInvoices: false })}
              />
            )}
            {openSnackbar.limit90 && (
              <Limit90Alert
                isFromPartner={subscription?.is_from_partner || false}
                partnerEmail={subscription?.partner_org_email || ''}
                meterNames={openSnackbar.limit90Meters}
                onClose={() => setOpenSnackbar({ ...openSnackbar, limit90: false })}
              />
            )}
            {openSnackbar.limit100 && (
              <Limit100Alert
                isFromPartner={subscription?.is_from_partner || false}
                partnerEmail={subscription?.partner_org_email || ''}
                meterNames={openSnackbar.limit100Meters}
              />
            )}
            {openSnackbar.noActiveSubscription && (
              <NoActiveSubscriptionAlert
                isFromPartner={subscription?.is_from_partner || false}
                partnerEmail={subscription?.partner_org_email || ''}
              />
            )}
          </Box>
          {!subscription?.is_from_partner && user?.is_root_user && (
            <Button
              sx={{
                fontSize: '0.8rem',
                width: 'fit-content',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              color="primary"
              variant="contained"
              onClick={() => navigate('/settings?tab=subscription')}
            >
              Manage Subscriptions
            </Button>
          )}
        </Stack>
      </Alert>
    </Box>
  );
};

export default BottomBar;
